import { Component, ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { OverlayModule } from '@angular/cdk/overlay';
import { KeycloakService } from 'keycloak-angular';
import { catchError, filter, forkJoin, map, of, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { ConfigService } from '../../../../my-shared-lib/src/lib/shared-services/config.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { ChatComponent as ChatAssistComponent } from '../chat/chat.component';

// Allow optional children for nested submenus
interface MenuItem {
  name: string;
  icon: string;
  path: string;
  children?: MenuItem[]; // <-- added
  dynamicChildren?: string;
  pdfkey?: string;
  surveyType?: string;
  disabled?: boolean;
}

interface Menu {
  [key: string]: MenuItem[];
}

interface NotificationFile {
  file_name: string;
  file_type: string;
  file_data: string; // base64 encoded string
}

interface Notification {
  notification_data: string;
  event_id: string;
  file: NotificationFile | null; // Can be an object or null if no file data
  read: boolean; // Keep track of read status
  icon: string; // Keep an icon for display purposes
  timestamp: Date; // Add timestamp
}

interface HelpFile {
  name: string;
  path: string;
}

@Component({
  selector: 'app-top-bar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    MatToolbarModule,
    OverlayModule,
    MatMenuModule,
    MatBadgeModule,
    MatDividerModule,
    MatButtonModule,
    ChatAssistComponent,
  ],
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss'],
})
export class TopBarComponent implements OnInit, OnDestroy {
  hoveredMenu: string | null = null;
  hoveredSubMenu: string | null = null; // For nested hover
  username: string | undefined;
  private menuTimeout: any;
  private isMenuAreaActive: boolean = false; // New flag to track if mouse is within the menu area
  isLandingPage: boolean = false;
  currentRoute: string = ''; // Variable to track the current route
  hoveredChildMenu: string | null = null;
  menu: any;
  filteredMenu: any = {};
  topBarConfig: any = {};
  // Create a routeMap that maps each route (including submenus) to its main menu key
  routeMap: { [key: string]: string } = {};
  allowedPaths: any;
  quarterYear = '';
  private surveyTypesLoaded = false;
  private surveyTypesLoading = false;

  userEmail: string | undefined;
  helpFiles: HelpFile[] = [
    { name: 'Product Health Index - User Manual', path: '/assets/User-Manual.pdf' },
    { name: 'Product Cost ($/HP) Computation Guide', path: '/assets/Product-Cost-Calculations.pdf' },
    { name: 'PHI Scoring Methodology v1.2', path: '/assets/PHI-Scoring-Methodology-v1.2.pdf' }
  ];

  notifications: Notification[] = [];

  private mapRoutesRecursive(menuItems: MenuItem[], mainMenu: string) {
    menuItems.forEach((item) => {
      this.routeMap[item.path] = mainMenu;
      if (item.children) {
        this.mapRoutesRecursive(item.children, mainMenu);
      }
    });
  }
  constructor(
    private router: Router,
    private keycloakService: KeycloakService,
    private cdr: ChangeDetectorRef,
    private service: ConfigService
  ) {
    this.menu = this.service.getMenuItems();
    this.topBarConfig = this.service.getTopBarConfig();
    this.getQuarter();
    Object.keys(this.menu).forEach((mainMenu) => {
      this.mapRoutesRecursive(this.menu[mainMenu], mainMenu);
    });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.isLandingPage =
          event.urlAfterRedirects === '/' ||
          event.urlAfterRedirects === '/landing-page';

        this.currentRoute = event.urlAfterRedirects || '';

        // AUTO-SET PDF KEY BASED ON ROUTE
        const pdfKey = this.findPdfKeyForRoute(this.currentRoute);
        if (pdfKey) {
          localStorage.setItem('key', pdfKey);
        }
      });
  }
  
  private destroy$ = new Subject<void>();
  private readonly NOTIFICATION_REFRESH_INTERVAL = 30000; // 30 seconds

  async ngOnInit(): Promise<void> {
    const isLoggedIn = await this.keycloakService.isLoggedIn();
    if (isLoggedIn) {
      const userDetails = await this.keycloakService.loadUserProfile();
      this.username = `${userDetails?.firstName} ${userDetails?.lastName}`.trim() || 'User';
      this.userEmail = this.keycloakService.getKeycloakInstance().idTokenParsed?.['email'];

      if (this.userEmail) {
        this.service.getRole(this.userEmail).subscribe((role: any) => {
          this.service.getMenu(role).subscribe((menu: any) => {
            this.allowedPaths = menu;
            this.filteredMenu = this.filterMenu(this.menu);
          });
        });

        timer(0, this.NOTIFICATION_REFRESH_INTERVAL).pipe(
          takeUntil(this.destroy$),
          switchMap(() => this.service.getNotification(this.userEmail!)),
          map((response: any) => response.events.map((event: any, index: number) => ({
            ...event,
            read: false,
            file: (event.file && event.file.file_data) ? event.file : null,
            icon: (event.file && event.file.file_data) ? 'file_download' : 'info',
            timestamp: new Date(new Date().getTime() - (index * 1000 * 60 * (5 + index)))
          }))),
          catchError(error => {
            console.error('Failed to poll notifications', error);
            return of([]);
          })
        ).subscribe({
          next: (notifications: Notification[]) => {
            this.notifications = notifications.reverse();
            this.cdr.detectChanges();
          }
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // onNotificationClick(notification: Notification): void {
  //   if (!this.userEmail) {
  //     console.error("User email is not available to process notification click.");
  //     return;
  //   }
  //   console.log('Notification clicked:', notification);
  //   notification.read = true;

  //   if (notification.file && notification.file.file_data) {
  //     this.downloadFile(notification);
  //   } else {
  //     const payload = {
  //       event_id: notification.event_id,
  //       status: 'success' // As per requirement, "as of now keep success"
  //     };
  //     this.service.getNotificationDetails(payload).subscribe({
  //       next: (response) => {
  //         console.log('API response:', response);
  //         // alert(response);
  //         // Remove the notification from the list after it's been processed
  //         this.notifications = this.notifications.filter(n => n.event_id !== notification.event_id);
  //         this.cdr.detectChanges(); // Update the view
  //       },
  //       error: (error) => {
  //         console.error('API error:', error)  ;
  //         // alert('Failed to get notification details.');
  //       },
  //     });
  //   }
  // }

  onNotificationClick(notification: Notification): void {
  if (!this.userEmail) {
    console.error("User email is not available to process notification click.");
    return;
  }

  console.log('Notification clicked:', notification);
  notification.read = true;

  const payload = {
    event_id: notification.event_id,
    status: 'success'
  };

  this.service.getNotificationDetails(payload).subscribe({
    next: (response) => {
      console.log('API response:', response);

      // If it has a file, download after API success
      if (notification.file && notification.file.file_data) {
        this.downloadFile(notification);
      }

      // Remove from list after processing
      this.notifications = this.notifications.filter(
        n => n.event_id !== notification.event_id
      );

      this.cdr.detectChanges();
    },
    error: (error) => {
      console.error('API error:', error);
    }
  });
}

  downloadFile(notification: Notification): void {
    if (!notification.file || !notification.file.file_data) {
      console.warn('No file data to download for this notification.');
      alert('No file data available for download.');
      return;
    }

    try {
      const byteCharacters = atob(notification.file.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: notification.file.file_type || 'application/octet-stream' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = notification.file.file_name || `notification_${notification.event_id}_file`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Error downloading file:', e);
      alert('Could not download the file. The file data may be corrupt or not in base64 format.');
    }
  }

  // markAllAsRead(): void {
  //   // Partition notifications into those with and without files
  //   const withFiles = this.notifications.filter(n => n.file && n.file.file_data);
  //   const withoutFiles = this.notifications.filter(n => !n.file || !n.file.file_data);

  //   // Immediately mark notifications with files as read on the client side
  //   withFiles.forEach(n => n.read = true);

  //   if (withoutFiles.length === 0) {
  //     this.cdr.detectChanges();
  //     return;
  //   }

  //   const updateCalls = withoutFiles.map(notification => {
  //     const payload = {
  //       event_id: notification.event_id,
  //       status: 'success'
  //     };
  //     return this.service.getNotificationDetails(payload).pipe(
  //       map(() => notification.event_id), // On success, pass the event_id
  //       catchError(error => {
  //         console.error(`Failed to mark notification ${notification.event_id} as read.`, error);
  //         return of(null); // In case of an error, return null
  //       })
  //     );
  //   });

  //   forkJoin(updateCalls).subscribe(successfulEventIds => {
  //     // Filter out nulls from failed API calls
  //     const idsToRemove = successfulEventIds.filter(id => id !== null);
      
  //     // Remove the notifications that were successfully updated on the backend
  //     const remainingWithoutFiles = withoutFiles.filter(n => !idsToRemove.includes(n.event_id));
      
  //     // For any that failed, just mark them as read on the client
  //     remainingWithoutFiles.forEach(n => n.read = true);
      
  //     // The new notifications list contains notifications with files and those that failed to update
  //     this.notifications = [...withFiles, ...remainingWithoutFiles];
      
  //     this.cdr.detectChanges();
  //   });
  // }

  markAllAsRead(): void {
  if (!this.notifications || this.notifications.length === 0) {
    return;
  }

  const updateCalls = this.notifications.map(notification => {
    const payload = {
      event_id: notification.event_id,
      status: 'success',
    };

    return this.service.getNotificationDetails(payload).pipe(
      map(() => notification.event_id),
      catchError(error => {
        console.error(
          `Failed to mark notification ${notification.event_id} as read.`,
          error
        );
        return of(null);
      })
    );
  });

  forkJoin(updateCalls).subscribe(successfulEventIds => {
    // Remove failed ones (nulls)
    const idsToRemove = successfulEventIds.filter(id => id !== null);

    // Keep only those that failed on backend
    this.notifications = this.notifications.filter(
      n => !idsToRemove.includes(n.event_id)
    );

    // Mark remaining (failed) notifications as read on client
    this.notifications.forEach(n => (n.read = true));

    this.cdr.detectChanges();
  });
}

  getUnreadNotificationCount(): number {
    return this.notifications.filter((n) => !n.read).length;
  }
  
  getQuarter() {
    this.service.getQuadrant().subscribe((data: any) => {
      this.quarterYear = data.quarterYear;
    });
  }

  shouldShowLastUpdated(): boolean {
    const config = this.topBarConfig?.lastUpdated;
    if (!config?.enabled) return false;
    if (this.currentRoute === '/landing-page' || this.currentRoute === '/') return true;

    const normalizedRoute = String(this.currentRoute || '').split('?')[0];
    const hiddenRoutes: string[] = config.hiddenRoutes || [];
    if (hiddenRoutes.includes(normalizedRoute)) {
      return false;
    }

    const currentMenu = this.routeMap[normalizedRoute] || this.routeMap[this.currentRoute];
    const hiddenMenus: string[] = config.hiddenMenus || [];
    if (currentMenu && hiddenMenus.includes(currentMenu)) {
      return false;
    }

    return true;
  }

  getLastUpdatedLabel(): string {
    if (this.currentRoute === '/landing-page') {
      return `Last Updated: ${this.quarterYear}`;
    }

    const config = this.topBarConfig?.lastUpdated;
    const dynamicMenus = Array.isArray(config?.dynamicQuarterMenu)
      ? config.dynamicQuarterMenu
      : [config?.dynamicQuarterMenu || 'Product Health'];

    if (dynamicMenus.some((menuName: string) => this.isActive(menuName))) {
      return `Last Updated: ${this.quarterYear}`;
    }

    const staticText = config?.staticText || 'March 2026';
    return `Last Updated: ${staticText}`;
  }
  findPdfKeyForRoute(route: string): string | null {
    const checkRecursive = (items: any[]): string | null => {
      for (const item of items) {
        if (item.path === route && item.pdfkey) {
          return item.pdfkey;
        }
        if (item.children) {
          const childResult = checkRecursive(item.children);
          if (childResult) return childResult;
        }
      }
      return null;
    };

    for (const key of Object.keys(this.menu)) {
      const result = checkRecursive(this.menu[key]);
      if (result) return result;
    }

    return null;
  }

  // Function to check if the current route matches the routeMap entry for this menu
  isActive(mainMenu: string): boolean {
    return this.routeMap[this.currentRoute] === mainMenu;
  }

  filterMenu(menu: any): any {
    const allowed = this.allowedPaths;

    const normalize = (path: string) =>
      path?.startsWith("/") ? path : "/" + path;

    const filterRecursive = (items: any[]): any[] => {
      return items
        .filter(item => !item.path || allowed.includes(normalize(item.path)))
        .map(item => ({
          ...item,
          children: item.children ? filterRecursive(item.children) : null
        }))
        .filter(item => !Array.isArray(item.children) || item.children.length > 0);
    };

    const result: any = {};
    for (const key of Object.keys(menu)) {
      result[key] = filterRecursive(menu[key]);
    }
    return result;
  }


  get menuKeys() {
    if (!this.allowedPaths) {
      return [];
    }

    const source = Object.keys(this.filteredMenu || {}).length
      ? this.filteredMenu
      : this.menu;
    return Object.keys(source).filter((key) => source[key]?.length > 0);
  }

  navigate(path: string, key: string) {
    if (this.menuTimeout) {
      clearTimeout(this.menuTimeout);
    }
    this.hoveredMenu = null;
    this.hoveredSubMenu = null;
    this.router.navigate([path]);
    this.cdr.detectChanges();
    localStorage.setItem("key", key)
  }

  navigateMenuItem(item: MenuItem) {
    if (!item || item.disabled) {
      return;
    }

    if (item.surveyType) {
      localStorage.setItem('selectedSurveyType', item.surveyType);
    } else if (item.dynamicChildren === 'openSurveyTypes') {
      this.loadOpenSurveyTypes();
      return;
    }

    this.navigate(item.path, item.pdfkey || '');
  }

  navigateSection(key: string) {
    if (this.menuTimeout) {
      clearTimeout(this.menuTimeout);
    }
    const first = this.menu[key]?.[0];
    if (first?.path) {
      this.hoveredMenu = null;
      this.hoveredSubMenu = null;
      this.router.navigate([first.path]);
      this.cdr.detectChanges();
    }
  }

  onMouseEnter(key: string) {
    this.hoveredMenu = key;
    this.isMenuAreaActive = true;
    this.cdr.detectChanges();
  }

  onSubMenuMouseEnter(item: MenuItem) {
    this.hoveredSubMenu = item.name;
    if (item.dynamicChildren === 'openSurveyTypes') {
      this.loadOpenSurveyTypes();
    }
  }

  private loadOpenSurveyTypes(): void {
    if (this.surveyTypesLoaded || this.surveyTypesLoading) {
      return;
    }

    this.surveyTypesLoading = true;
    this.updateSurveyMenuChildren([
      {
        name: 'Loading surveys...',
        icon: 'hourglass_empty',
        path: '/survey',
        disabled: true,
      },
    ]);

    this.service
      .getDashboardConfig('settings/list-of-survey.json')
      .pipe(
        switchMap((config: any) => {
          const tableConfig = this.findSurveyListTableConfig(config);
          const readAction = this.resolveSurveyReadAction(tableConfig);
          if (!readAction?.payload) {
            return of([]);
          }

          const payload = this.replacePayloadTokens(readAction.payload);
          return this.service.customApi(
            readAction.url || tableConfig?.apiUrl || '/spriced/platform',
            payload,
            tableConfig,
            readAction.method || 'post'
          ).pipe(
            map((response: any) => this.extractOpenSurveyTypes(response, tableConfig))
          );
        }),
        catchError((error) => {
          console.error('Failed to load published survey types', error);
          return of([]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((surveyTypes: string[]) => {
        this.surveyTypesLoading = false;
        this.surveyTypesLoaded = true;

        const children = surveyTypes.length
          ? surveyTypes.map((surveyType) => ({
              name: surveyType,
              icon: 'assignment',
              path: '/survey',
              pdfkey: 'product-metrix',
              surveyType,
            }))
          : [
              {
                name: 'No published surveys',
                icon: 'info',
                path: '/survey',
                disabled: true,
              },
            ];

        this.updateSurveyMenuChildren(children);
        this.cdr.detectChanges();
      });
  }

  private findSurveyListTableConfig(config: any): any {
    const rows = Array.isArray(config?.pageContent) ? config.pageContent : [];
    for (const row of rows) {
      const columns = Array.isArray(row?.columns) ? row.columns : [];
      const tableConfig = columns.find(
        (column: any) => column?.widgetId === 'surveyListTable'
      );
      if (tableConfig) {
        return tableConfig;
      }
    }

    return null;
  }

  private resolveSurveyReadAction(tableConfig: any): any {
    if (tableConfig?.initialLoadAction?.payload) {
      return tableConfig.initialLoadAction;
    }

    const actionIndex = Number(tableConfig?.initialLoadPostSaveActionIndex);
    if (
      Number.isInteger(actionIndex) &&
      Array.isArray(tableConfig?.postSaveActions)
    ) {
      return tableConfig.postSaveActions[actionIndex];
    }

    return Array.isArray(tableConfig?.postSaveActions)
      ? tableConfig.postSaveActions[0]
      : null;
  }

  private replacePayloadTokens(value: any): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.replacePayloadTokens(item));
    }

    if (value && typeof value === 'object') {
      return Object.entries(value).reduce((acc: Record<string, any>, [key, item]) => {
        acc[key] = this.replacePayloadTokens(item);
        return acc;
      }, {});
    }

    if (typeof value !== 'string') {
      return value;
    }

    const currentYear = new Date().getFullYear();
    if (value === '__YEAR__' || value === '__CURRENT_YEAR__') {
      return currentYear;
    }

    return value.replace(/__YEAR__|__CURRENT_YEAR__/g, String(currentYear));
  }

  private extractOpenSurveyTypes(response: any, tableConfig: any): string[] {
    const responseData = response?.responseList?.[0]?.responseData;
    const primaryPayload = Array.isArray(responseData) ? responseData[0] : responseData;
    const respKey = tableConfig?.respKey;
    const candidates = respKey
      ? [
          primaryPayload?.[respKey],
          response?.responseList?.[0]?.[respKey],
          response?.[respKey],
          responseData,
          response?.responseData,
        ]
      : [responseData, response?.responseData, primaryPayload, response];

    const rows = candidates.find((candidate) => Array.isArray(candidate)) || [];
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

    return Array.from(
      new Set(
        rows
          .filter((row: any) => {
            const status = String(row?.status || '').trim().toLowerCase();
            const isPublished = status === 'published';
            const startDate = this.toDateOnly(row?.start_date);
            const endDate = this.toDateOnly(row?.end_date);
            return (
              isPublished &&
              (!startDate || startDate.getTime() <= todayOnly) &&
              (!endDate || endDate.getTime() >= todayOnly)
            );
          })
          .map((row: any) => String(row?.survey_type || '').trim())
          .filter((surveyType: string) => !!surveyType)
      )
    );
  }

  private toDateOnly(value: any): Date | null {
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }

  private updateSurveyMenuChildren(children: MenuItem[]): void {
    const updateRecursive = (items: MenuItem[] = []) => {
      items.forEach((item) => {
        if (item.dynamicChildren === 'openSurveyTypes') {
          item.children = children;
        }
        if (item.children) {
          updateRecursive(item.children);
        }
      });
    };

    updateRecursive(this.menu?.['Product Health']);
    updateRecursive(this.filteredMenu?.['Product Health']);
  }

  onMouseLeave() {
    this.isMenuAreaActive = false;
    this.menuTimeout = setTimeout(() => {
      if (!this.isMenuAreaActive) {
        this.hoveredMenu = null;
        this.hoveredSubMenu = null;
        this.hoveredChildMenu = null;
        this.cdr.detectChanges();
      }
    }, 200);
  }

  onSubmenuContainerMouseEnter() {
    if (this.menuTimeout) {
      clearTimeout(this.menuTimeout);
    }
    this.isMenuAreaActive = true; // Mouse entered submenu container
  }

  onSubmenuContainerMouseLeave() {
    this.isMenuAreaActive = false; // Mouse left submenu container
    this.menuTimeout = setTimeout(() => {
      if (!this.isMenuAreaActive) { // Only close if mouse is not in any part of the menu area
        this.hoveredMenu = null;
        this.hoveredSubMenu = null;
        this.hoveredChildMenu = null;
        this.cdr.detectChanges();
      }
    }, 200);
  }

  async logout(): Promise<void> {
    await this.keycloakService.logout(window.location.origin);
    localStorage.clear();
  }
  openHelpFile(path: string): void {
    window.open(path, '_blank');
  }
}

function JSON_stringify(response: any, arg1: null, arg2: number) {
  throw new Error('Function not implemented.');
}
